import { localDayAt, mondayWeekRange, parseIsoInstant } from './time'
import type { MetricRecord, MetricType, SleepRecord, WorkoutRecord } from './types'

export interface SnapshotMetric {
  record: MetricRecord
  isHistorical: boolean
  comparison?: DailyMetricComparison
}

export interface DailyMetricComparison {
  current: MetricRecord | null
  previous: MetricRecord | null
  currentDay?: string
  previousDay?: string
  isYesterday?: boolean
}

/** Latest recorded day compared with the previous recorded day, across gaps. */
export function dailyMetricComparison(records: readonly MetricRecord[], type: 'weight' | 'bodyFat', referenceAt: string, timeZone: string): DailyMetricComparison {
  const today = localDayAt(referenceAt, timeZone)
  const now = parseIsoInstant(referenceAt).getTime()
  const available = records.filter(record => record.type === type && parseIsoInstant(record.recordedAt).getTime() <= now)
  const current = latestByInstant(available, record => record.recordedAt)
  if (!current) return { current: null, previous: null }
  const currentDay = localDayAt(current.recordedAt, timeZone)
  const previous = latestByInstant(available.filter(record => localDayAt(record.recordedAt, timeZone) < currentDay), record => record.recordedAt)
  const previousDay = previous ? localDayAt(previous.recordedAt, timeZone) : undefined
  return { current, previous, currentDay, previousDay, isYesterday: currentDay === today && previousDay === addLocalDays(today, -1) }
}

export interface TodaySnapshot {
  localDay: string
  weight: SnapshotMetric | null
  bodyFat: SnapshotMetric | null
  activeEnergy: MetricRecord | null
  sleep: SleepRecord | null
}

export interface MetricTrendPoint {
  localDay: string
  value: number | null
}

export interface WorkoutWeekSummary {
  count: number
  durationMinutes: number
  activeEnergyKcal: number
  energyRecordedCount: number
}

function addLocalDays(localDay: string, days: number): string {
  const [yearText, monthText, dayText] = localDay.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const day = Number(dayText)
  const result = new Date(Date.UTC(year, month - 1, day + days))
  return `${String(result.getUTCFullYear()).padStart(4, '0')}-${String(
    result.getUTCMonth() + 1,
  ).padStart(2, '0')}-${String(result.getUTCDate()).padStart(2, '0')}`
}

function shouldReplaceLatest<T extends { id: string }>(
  candidate: T,
  current: T | null | undefined,
  instant: (record: T) => string,
): boolean {
  if (current === null || current === undefined) return true

  const candidateTime = parseIsoInstant(instant(candidate)).getTime()
  const currentTime = parseIsoInstant(instant(current)).getTime()
  return candidateTime > currentTime || (candidateTime === currentTime && candidate.id > current.id)
}

function latestByInstant<T extends { id: string }>(
  records: readonly T[],
  instant: (record: T) => string,
): T | null {
  let latest: T | null = null

  for (const record of records) {
    if (shouldReplaceLatest(record, latest, instant)) {
      latest = record
    }
  }

  return latest
}

function latestMetricForDay(
  records: readonly MetricRecord[],
  type: MetricType,
  localDay: string,
  timeZone: string,
): MetricRecord | null {
  return latestByInstant(
    records.filter((record) => record.type === type && localDayAt(record.recordedAt, timeZone) === localDay),
    (record) => record.recordedAt,
  )
}

function metricSnapshot(
  records: readonly MetricRecord[],
  type: 'weight' | 'bodyFat',
  today: string,
  timeZone: string,
): SnapshotMetric | null {
  const todayRecord = latestMetricForDay(records, type, today, timeZone)
  if (todayRecord !== null) return { record: todayRecord, isHistorical: false }

  const historical = latestByInstant(
    records.filter(
      (record) => record.type === type && localDayAt(record.recordedAt, timeZone) < today,
    ),
    (record) => record.recordedAt,
  )
  return historical === null ? null : { record: historical, isHistorical: true }
}

export function selectTodaySnapshot(
  metricRecords: readonly MetricRecord[],
  sleepRecords: readonly SleepRecord[],
  referenceAt: string,
  timeZone: string,
): TodaySnapshot {
  const localDay = localDayAt(referenceAt, timeZone)
  const available = metricRecords.filter(record => parseIsoInstant(record.recordedAt).getTime() <= parseIsoInstant(referenceAt).getTime())
  const withComparison = (type: 'weight' | 'bodyFat'): SnapshotMetric | null => {
    const snapshot = metricSnapshot(available, type, localDay, timeZone)
    return snapshot === null ? null : { ...snapshot, comparison: dailyMetricComparison(available, type, referenceAt, timeZone) }
  }

  return {
    localDay,
    weight: withComparison('weight'),
    bodyFat: withComparison('bodyFat'),
    activeEnergy: latestMetricForDay(metricRecords, 'activeEnergy', localDay, timeZone),
    sleep: latestByInstant(
      sleepRecords.filter((record) => record.wakeLocalDay === localDay),
      (record) => record.endAt,
    ),
  }
}

export function buildMetricTrend(
  records: readonly MetricRecord[],
  type: MetricType,
  days: number,
  referenceAt: string,
  timeZone: string,
): MetricTrendPoint[] {
  const today = localDayAt(referenceAt, timeZone)
  const dailyRecords = new Map<string, MetricRecord>()

  for (const record of records) {
    if (record.type !== type) continue
    const localDay = localDayAt(record.recordedAt, timeZone)
    const existing = dailyRecords.get(localDay)
    if (shouldReplaceLatest(record, existing, (candidate) => candidate.recordedAt)) {
      dailyRecords.set(localDay, record)
    }
  }

  const start = addLocalDays(today, 1 - days)
  return Array.from({ length: days }, (_, index) => {
    const localDay = addLocalDays(start, index)
    return { localDay, value: dailyRecords.get(localDay)?.value ?? null }
  })
}

export function buildSleepTrend(
  records: readonly SleepRecord[],
  days: number,
  referenceAt: string,
  timeZone: string,
): MetricTrendPoint[] {
  const today = localDayAt(referenceAt, timeZone)
  const dailyRecords = new Map<string, SleepRecord>()

  for (const record of records) {
    const existing = dailyRecords.get(record.wakeLocalDay)
    if (shouldReplaceLatest(record, existing, (candidate) => candidate.endAt)) {
      dailyRecords.set(record.wakeLocalDay, record)
    }
  }

  const start = addLocalDays(today, 1 - days)
  return Array.from({ length: days }, (_, index) => {
    const localDay = addLocalDays(start, index)
    const record = dailyRecords.get(localDay)
    return {
      localDay,
      value: record === undefined ? null : Math.round((record.durationMinutes / 60) * 10) / 10,
    }
  })
}

export function summarizeWorkoutWeek(
  records: readonly WorkoutRecord[],
  referenceAt: string,
  timeZone: string,
): WorkoutWeekSummary {
  const range = mondayWeekRange(referenceAt, timeZone)
  const start = parseIsoInstant(range.startInclusive).getTime()
  const end = parseIsoInstant(range.endExclusive).getTime()
  let count = 0
  let durationMinutes = 0
  let activeEnergyKcal = 0
  let energyRecordedCount = 0

  for (const record of records) {
    const startedAt = parseIsoInstant(record.startedAt).getTime()
    if (record.status === 'planned') continue
    if (startedAt < start || startedAt >= end) continue
    count += 1
    durationMinutes += record.durationMinutes
    if (record.activeEnergyKcal !== undefined) {
      activeEnergyKcal += record.activeEnergyKcal
      energyRecordedCount += 1
    }
  }

  return { count, durationMinutes, activeEnergyKcal, energyRecordedCount }
}
